/**
 * AUTH MODAL — modal logowania/rejestracji
 * Konwencja: Sikorski (H = si naturalne, B = si bemol)
 */

import { useState } from 'react'

import { useAuth } from '../hooks/useAuth'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  defaultTab?: 'login' | 'register'
}

export function AuthModal({ isOpen, onClose, defaultTab = 'login' }: AuthModalProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>(defaultTab)
  const { login, register, isLoading, error, clearError } = useAuth()

  // Form states
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [joinCode, setJoinCode] = useState('')

  if (!isOpen) return null

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    const success = await login({ email, password })
    if (success) {
      onClose()
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    const success = await register({
      email,
      password,
      displayName: displayName || email.split('@')[0],
      role: 'student',
      inviteCode: joinCode.trim() || undefined,
    })

    if (success) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="manuscript-card w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
      >
        {/* Header */}
        <div className="p-4 border-b border-ink/10">
          <h2 id="auth-modal-title" className="font-display text-2xl text-ink">
            {activeTab === 'login' ? 'Logowanie' : 'Rejestracja'}
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-ink/10">
          <button
            onClick={() => setActiveTab('login')}
            className={`flex-1 py-3 font-ui text-sm transition-colors ${
              activeTab === 'login'
                ? 'text-ochre border-b-2 border-ochre'
                : 'text-ink/70 hover:text-ink'
            }`}
          >
            Logowanie
          </button>
          <button
            onClick={() => setActiveTab('register')}
            className={`flex-1 py-3 font-ui text-sm transition-colors ${
              activeTab === 'register'
                ? 'text-ochre border-b-2 border-ochre'
                : 'text-ink/70 hover:text-ink'
            }`}
          >
            Rejestracja
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-burgundy/10 border-b border-burgundy/20">
            <p className="font-ui text-sm text-burgundy">{error}</p>
          </div>
        )}

        {/* Login Form */}
        {activeTab === 'login' && (
          <form onSubmit={handleLogin} className="p-4 space-y-4">
            <div>
              <label htmlFor="login-email" className="font-ui text-sm text-ink/70 block mb-1">Email</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field w-full"
                required
              />
            </div>
            <div>
              <label htmlFor="login-password" className="font-ui text-sm text-ink/70 block mb-1">Hasło</label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field w-full"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full disabled:opacity-50"
            >
              {isLoading ? 'Logowanie...' : 'Zaloguj się'}
            </button>
          </form>
        )}

        {/* Register Form */}
        {activeTab === 'register' && (
          <form onSubmit={handleRegister} className="p-4 space-y-4">
            <div>
              <label htmlFor="reg-email" className="font-ui text-sm text-ink/70 block mb-1">Email *</label>
              <input
                id="reg-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field w-full"
                required
              />
            </div>
            <div>
              <label htmlFor="reg-password" className="font-ui text-sm text-ink/70 block mb-1">Hasło *</label>
              <input
                id="reg-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field w-full"
                required
                minLength={6}
                aria-describedby="reg-password-hint"
              />
              <p id="reg-password-hint" className="font-ui text-xs text-ink/70 mt-1">Minimum 6 znaków</p>
            </div>
            <div>
              <label htmlFor="reg-displayname" className="font-ui text-sm text-ink/70 block mb-1">Nazwa wyświetlana</label>
              <input
                id="reg-displayname"
                type="text"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="input-field w-full"
                placeholder="Imię i nazwisko"
              />
            </div>
            <div>
              <label htmlFor="reg-invitecode" className="font-ui text-sm text-ink/70 block mb-1">
                Kod zaproszenia
              </label>
              <input
                id="reg-invitecode"
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="input-field w-full"
                placeholder="np. ABC123"
                maxLength={8}
                aria-describedby="reg-invitecode-hint"
              />
              <p id="reg-invitecode-hint" className="font-ui text-xs text-ink/70 mt-1">
                Kod do klasy od nauczyciela — lub kod nauczyciela jeśli zakładasz konto pedagoga
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full disabled:opacity-50"
            >
              {isLoading ? 'Rejestracja...' : 'Zarejestruj się'}
            </button>
          </form>
        )}

        {/* Close button */}
        <div className="p-4 border-t border-ink/10 text-center">
          <button
            onClick={onClose}
            className="font-ui text-sm text-ink/70 hover:text-ink"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  )
}
